/* Notification Center — host page (AI-only design module, demo data).
 *
 * Structured like Performance Analytics: the sub-modules live as nav items in
 * the app's OWN left sidebar (`grp-notifications` in layouts/navData.ts), and
 * this component renders just the ONE selected sub-page (`subId`).
 *
 * All toolbar buttons are wired (parity with the standalone HTML prototype):
 * New opens the wizard / entity form, Edit edits the selected row, Delete asks
 * for confirmation then removes, Refresh / Export / Import raise the shared
 * toast — via useNotify / useConfirm.  Mutations go through the Zustand store
 * (features/notifications/store.ts) so they persist across sub-page nav. */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import './notifications.css';
import { useNotify } from '../../components/notifications';
import { useConfirm } from '../../components/confirm';
import { FormTitle } from '../../components/FormTitle';
import { AppToolbar } from '../../components/AppToolbar';
import { useHotkeys, formatCombo } from '../../components/hotkeys';
import { useNcStore, summaryOf } from './store';
import { effectiveAck } from './deliveryMethod';
import { categoryCodeLabel } from './categoryCodes';
import { incompleteMessage, savedRuleMissing } from './ruleCompleteness';
import { getDeliveries, resendDelivery, apiErrorText, type DeliveryPage as NcDeliveryPage } from './api';
import { runLoading, BusyIcon } from '../../components/loading';
import { useAuthStore } from '../../auth/store';
import { fmtLocalShort } from '../../utils/datetime';
import { PURPOSE_LABEL, NotificationRulesAuditForm, NotificationSettingsAuditForms, type NcCategory, type NcRule, type NcDelivery, type Severity, type DeliveryStatus } from './data';
import { NewRuleWizard, CategoryForm, RecipientForm, TemplateForm } from './dialogs';
import RuleDetailPage from './RuleDetail';
import PopupSettingsPage from './inbox/PopupSettingsPage';
import { listPopupSettings, type PopupSetting } from './inbox/inboxApi';

/* ── small shared bits ── */
function SevPill({ s }: { s: Severity }) {
  const label = s === 'critical' ? 'Critical' : s === 'warning' ? 'Warning' : 'Info';
  return <span className={`nc-sev ${s}`}><span className="dot" />{label}</span>;
}
function DeliveryPill({ s }: { s: DeliveryStatus }) {
  return <span className={`nc-status ${s === 'Failed' || s === 'Bounced' ? 'fail' : 'ok'}`}>{s}</span>;
}
function NcTopbar({ icon, title, sub, auditFormName, children }: {
  icon: string; title: string; sub?: string;
  /** Shows the shared, config-gated Audit chip for this dbo.AuditData FormName. */
  auditFormName?: string;
  children: ReactNode;
}) {
  return (
    <div className="nc-topbar">
      <div className="nc-title-wrap">
        {/* Shared <FormTitle> (icon + title from the same glyph as the sidebar) —
            single source of truth; replaces the bespoke nc-title + fake load chip. */}
        <FormTitle icon={icon} title={title} auditFormName={auditFormName} />
        {sub && <div className="nc-sub">{sub}</div>}
      </div>
      {/* Shared action bar — the sub-view's form-specific buttons in the left slot. */}
      <div className="nc-actions"><AppToolbar left={children} /></div>
    </div>
  );
}
function ExportButton({ onPick }: { onPick: (fmt: string) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="nc-export">
      <button className="ca-btn" onClick={() => setOpen((o) => !o)} onBlur={() => setTimeout(() => setOpen(false), 160)} title="Export to">
        <span className="tb-i tb-i-teal">📤</span> Export to ▾
      </button>
      {open && (
        <div className="nc-menu">
          {['CSV', 'Excel', 'PDF'].map((f) => (
            <div key={f} className="nc-menu-item" onMouseDown={() => { onPick(f); setOpen(false); }}><i className="ti ti-download" /> {f}</div>
          ))}
        </div>
      )}
    </div>
  );
}
function SearchBox({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <div className="list-search"><span className="tb-i">🔍</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
      {value && <span className="list-search-clear" onClick={() => onChange('')}>×</span>}
    </div>
  );
}

/* ════════════════════ OVERVIEW ════════════════════ */
/** The New Rule wizard's Create: the server saves the rule and mints its code, and only THEN does the
 *  wizard close — on a failure it stays open with everything the user entered. A second click while the
 *  first is saving is ignored, so one Create can never make two rules. */
function useCreateRule() {
  const notify = useNotify();
  const busy = useRef(false);
  return (categoryName: string, rule: NcRule, onDone: () => void) => {
    if (busy.current) return;
    busy.current = true;
    void useNcStore.getState().createRule(categoryName, rule)
      .then((saved) => {
        if (!saved) return;
        onDone();
        // A draft (Save as draft in the wizard) is saved OFF and says so.
        notify.success(saved.enabled
          ? `Rule "${saved.name}" created.`
          : `Rule "${saved.name}" saved as a draft — it is off until you finish it and turn it on.`);
      })
      .finally(() => { busy.current = false; });
  };
}

function OverviewPage({ onOpen }: { onOpen?: (id: string) => void }) {
  const categories = useNcStore((s) => s.categories);
  const recipients = useNcStore((s) => s.recipients);
  const templates = useNcStore((s) => s.templates);
  const deliveries = useNcStore((s) => s.deliveries);
  const deliveryStats = useNcStore((s) => s.deliveryStats);
  const loaded = useNcStore((s) => s.loaded);
  const createRule = useCreateRule();
  const notify = useNotify();
  const refresh = () => { void useNcStore.getState().load().then(() => notify.success('Overview refreshed.')); };
  const [wizard, setWizard] = useState(false);
  const [popupList, setPopupList] = useState<PopupSetting[] | null>(null);
  useEffect(() => { let live = true; listPopupSettings().then((l) => { if (live) setPopupList(l); }).catch(() => { if (live) setPopupList(null); }); return () => { live = false; }; }, []);
  const popupDefault = popupList?.find((p) => p.isDefault);
  const sum = summaryOf(categories, recipients, templates);
  const maxRules = Math.max(1, ...categories.map((c) => c.rules.length));
  const go = (id: string) => onOpen?.(id);
  // Rule-derived counts show '—' until the API responds — never a mock number.
  const rd = (n: number): number | string => (loaded ? n : '—');

  const cards = [
    { id: 'rules', icon: 'ti-adjustments-horizontal', count: rd(sum.totalRules), name: 'Notification Rules', desc: 'Standard rules across categories — set recipients & enable the ones this lab needs.' },
    { id: 'categories', icon: 'ti-category-2', count: rd(sum.categories), name: 'Rule Categories', desc: 'Category groupings that organise the rules list, sidebar & templates.' },
    { id: 'recipients', icon: 'ti-users-group', count: sum.recipientGroups, name: 'Recipient Groups', desc: 'Reusable recipient sets — define once, attach to many rules.' },
    { id: 'templates', icon: 'ti-mail', count: sum.templates, name: 'Email Templates', desc: 'Subject & body templates per category, with token placeholders.' },
    { id: 'delivery', icon: 'ti-mail-fast', count: deliveryStats.last24h, name: 'Delivery Log', desc: 'Read-only per-recipient delivery & failures for troubleshooting.' },
    { id: 'popup-settings', icon: 'ti-bell-cog', count: popupList ? popupList.length : '—', name: 'Pop-up Settings', desc: popupDefault ? `Named in-app (bell) alert pop-up settings. Default: ${popupDefault.name}.` : 'Named in-app (bell) alert pop-up settings — position and how each closes.' },
  ];

  // Keyboard shortcuts (CLAUDE.md standing rule 14, §18.6, ADR-0013): the
  // hand-rolled Overview toolbar (nothing is auto-bound — the AppToolbar carries
  // these as left-slot buttons).  Registered unconditionally above the return.
  useHotkeys([
    { combo: 'alt+n', label: 'New Rule', group: 'Notification Center', order: 1,
      run: () => setWizard(true) },
    { combo: 'alt+r', label: 'Refresh', group: 'Notification Center', order: 5,
      run: refresh },
  ]);

  return (
    <div className="nc-root">
      <NcTopbar icon="ti-layout-dashboard" title="Notification Center">
        <button className="ca-btn" onClick={() => setWizard(true)} title={`New Rule (${formatCombo('alt+n')})`}><span className="tb-i">＋</span> New Rule</button>
        <button className="ca-btn" onClick={refresh} title={`Refresh (${formatCombo('alt+r')})`}><span className="tb-i tb-i-green">🔄</span> Refresh</button>
      </NcTopbar>
      <div className="nc-scroll nc-overview">
        <div className="nc-kpi-row">
          <div className="nc-kpi green"><div className="nc-kpi-lbl">Active Rules</div><div className="nc-kpi-val green">{rd(sum.activeRules)}</div><div className="nc-kpi-meta">{loaded ? <>of {sum.totalRules} · {sum.disabledRules} disabled</> : 'Loading…'}</div></div>
          <div className="nc-kpi crit"><div className="nc-kpi-lbl">Require Acknowledgment</div><div className="nc-kpi-val crit">{rd(sum.requireAck)}</div><div className="nc-kpi-meta">Regulatory exceedance + hold-time</div></div>
          <div className="nc-kpi info"><div className="nc-kpi-lbl">Rule Categories</div><div className="nc-kpi-val">{rd(sum.categories)}</div><div className="nc-kpi-meta">{loaded ? <>{sum.activeCategories} active · {sum.categories - sum.activeCategories} disabled</> : 'Loading…'}</div></div>
          <div className="nc-kpi warn"><div className="nc-kpi-lbl">Delivery Failures (24h)</div><div className="nc-kpi-val warn">{loaded ? deliveryStats.failed24h : '—'}</div><div className="nc-kpi-meta">{loaded ? <>of {deliveryStats.last24h} sent{deliveryStats.last24h > 0 ? ` · ${Math.round((1 - deliveryStats.failed24h / deliveryStats.last24h) * 100)}% delivered` : ''}</> : 'Loading…'}</div></div>
        </div>

        <div className="nc-section"><i className="ti ti-apps" /> Configuration modules <span className="nc-section-sub">Click a module to open it</span></div>
        <div className="nc-cards">
          {cards.map((c) => (
            <div key={c.id} className="nc-card" onClick={() => go(c.id)}>
              <div className="nc-card-top"><div className="nc-card-ic"><i className={`ti ${c.icon}`} /></div><div className="nc-card-count">{c.count}</div></div>
              <div className="nc-card-name">{c.name}</div>
              <div className="nc-card-desc">{c.desc}</div>
              <div className="nc-card-cta">Open <i className="ti ti-arrow-right" /></div>
            </div>
          ))}
        </div>

        <div className="nc-grid2">
          <div className="nc-panel">
            <div className="nc-panel-hd"><div><div className="nc-panel-title"><i className="ti ti-chart-bar" /> Rules by Category</div><div className="nc-panel-sub">{loaded ? <>{sum.totalRules} rules across {sum.categories} categories</> : 'Loading…'}</div></div></div>
            <div className="nc-panel-bd">
              {!loaded ? (
                <div className="nc-state-row"><span className="nc-inline-spin" /> Loading…</div>
              ) : categories.length === 0 ? (
                <div className="nc-state-row">No categories yet.</div>
              ) : categories.map((c) => (
                <div key={c.code} className={`nc-cat-row${c.active ? '' : ' off'}`} onClick={() => go(`cat-${c.code}`)}>
                  <span className="nc-cat-ic"><i className={`ti ${c.icon}`} /></span>
                  <span className="nc-cat-name">{c.name}</span>
                  <span className="nc-cat-bar"><span className="nc-cat-fill" style={{ width: `${Math.round((c.rules.length / maxRules) * 100)}%` }} /></span>
                  <span className="nc-cat-num">{c.rules.length} rule{c.rules.length === 1 ? '' : 's'}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="nc-panel">
            <div className="nc-panel-hd"><div><div className="nc-panel-title"><i className="ti ti-mail-fast" /> Recent Delivery Activity</div><div className="nc-panel-sub">Last deliveries recorded by the runtime — read-only</div></div>
              <button className="ca-btn" onClick={() => go('delivery')}>View log <i className="ti ti-arrow-right" /></button></div>
            <table className="nc-tbl">
              <thead><tr><th>Time</th><th>Rule</th><th>Channel</th><th>Status</th></tr></thead>
              <tbody>
                {deliveries.length === 0 && <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--nc-text-light)', padding: '14px 0' }}>{loaded ? 'No deliveries recorded yet.' : 'Loading…'}</td></tr>}
                {deliveries.slice(0, 5).map((d, i) => (
                  <tr key={i}><td className="nc-mono">{fmtLocalShort(d.time)}</td><td>{d.rule}</td><td><span className="nc-chan on">{d.channel}</span></td><td><DeliveryPill s={d.status} /></td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {wizard && <NewRuleWizard categories={categories} onClose={() => setWizard(false)}
        onCreate={(cn, r) => createRule(cn, r, () => { setWizard(false); onOpen?.('rules'); })} />}
    </div>
  );
}

/* ════════════════════ NOTIFICATION RULES (+ category-filtered) ════════════════════ */
function RulesPage({ onOpen, filterCode }: { onOpen?: (id: string) => void; filterCode?: number }) {
  const categories = useNcStore((s) => s.categories);
  const recipients = useNcStore((s) => s.recipients);
  const templates = useNcStore((s) => s.templates);
  const loaded = useNcStore((s) => s.loaded);
  const error = useNcStore((s) => s.error);
  const createRule = useCreateRule();
  const deleteRuleCodes = useNcStore((s) => s.deleteRuleCodes);
  const toggleRule = useNcStore((s) => s.toggleRule);
  const notify = useNotify();
  // Refresh re-hydrates the rules+categories tree from the DB (the shared api
  // client drives the global loader).
  const refresh = () => { void useNcStore.getState().load().then(() => notify.success('Rules refreshed.')); };
  const { confirm } = useConfirm();

  const [sel, setSel] = useState<Set<string>>(new Set());
  const [wizard, setWizard] = useState(false);
  const [detail, setDetail] = useState<NcRule | null>(null);
  // Bumped by Rule Detail → Refresh so the page re-opens on the freshly loaded rule.
  const [detailKey, setDetailKey] = useState(0);
  // How the Rule Detail opens: the toolbar Edit → straight into Edit mode; the rule name → View.
  const [detailMode, setDetailMode] = useState<'view' | 'edit'>('view');
  const openDetail = (r: NcRule, mode: 'view' | 'edit') => { setDetailMode(mode); setDetail(r); };
  const [search, setSearch] = useState('');
  const [chip, setChip] = useState<'all' | 'internal' | 'external' | 'disabled'>('all');

  const sum = summaryOf(categories, recipients, templates);
  const cat = filterCode ? categories.find((c) => c.code === filterCode) : undefined;
  const baseCats = filterCode ? categories.filter((c) => c.code === filterCode) : categories;
  const term = search.trim().toLowerCase();
  const matchTerm = (r: NcRule) => !term || r.name.toLowerCase().includes(term) || r.code.toLowerCase().includes(term) || r.recipients.toLowerCase().includes(term);
  const matchChip = (r: NcRule) => chip === 'all' ? true : chip === 'internal' ? !r.outbound : chip === 'external' ? !!r.outbound : !r.enabled;
  const viewCats = baseCats
    .map((c) => ({ ...c, rules: c.rules.filter((r) => matchTerm(r) && matchChip(r)) }))
    .filter((c) => c.rules.length > 0);
  const shownRules = viewCats.flatMap((c) => c.rules);
  const allCodes = shownRules.map((r) => r.code);

  const toggleSel = (code: string) => setSel((cur) => { const n = new Set(cur); if (n.has(code)) n.delete(code); else n.add(code); return n; });
  const allChecked = allCodes.length > 0 && allCodes.every((c) => sel.has(c));

  function doEdit() {
    if (sel.size !== 1) { notify.warning(sel.size ? 'Select just one rule to edit.' : 'Select a rule to edit (tick its checkbox), or click the rule name.'); return; }
    const r = shownRules.find((x) => x.code === [...sel][0]);
    if (r) openDetail(r, 'edit');
  }
  function doDelete() {
    const codes = [...sel];
    if (!codes.length) { notify.warning('Select one or more rules to delete.'); return; }
    confirm({ title: codes.length === 1 ? 'Delete rule?' : 'Delete rules?', message: `Delete ${codes.length} rule${codes.length === 1 ? '' : 's'}? This cannot be undone.`, tone: 'danger', confirmLabel: 'Delete' })
      .then((ok) => { if (ok) { deleteRuleCodes(codes); setSel(new Set()); notify.success(codes.length === 1 ? 'Rule deleted.' : `${codes.length} rules deleted.`); } });
  }

  const title = cat ? cat.name : 'Notification Rules';
  // The "filtered to …" / description subtitle is dropped (user request
  // 2026-09-19); the NcTopbar shows no subtitle.

  // Keyboard shortcuts (CLAUDE.md standing rule 14, §18.6, ADR-0013): the
  // hand-rolled Rules toolbar.  Registered unconditionally ABOVE the first early
  // return; gated to the LIST screen (empty while the Rule Detail page shows, so
  // its buttons aren't shadowed) — each mirroring its button's handler.
  // (Import from library + Alt+I removed 2026-10-16: a placeholder that only showed a
  // success message and imported nothing; no default-rule library exists yet.)
  useHotkeys(
    detail
      ? []
      : [
          { combo: 'alt+n', label: 'New Rule', group: 'Notification Center', order: 1, run: () => setWizard(true) },
          { combo: 'alt+e', label: 'Edit rule', group: 'Notification Center', order: 2, run: () => doEdit() },
          { combo: 'alt+d', label: 'Delete rule', group: 'Notification Center', order: 3, run: () => doDelete() },
          { combo: 'alt+r', label: 'Refresh', group: 'Notification Center', order: 5, run: refresh },
        ],
  );

  // Edit / clicking a rule name opens the full Rule Detail page (HTML parity).
  if (detail) {
    const dCat = categories.find((c) => c.rules.some((r) => r.code === detail.code))?.name ?? cat?.name ?? '';
    // Refresh: reload from the database and re-open the rule with what is stored now.
    const refreshDetail = () => {
      const code = detail.code;
      void useNcStore.getState().load().then(() => {
        const st = useNcStore.getState();
        if (st.error) { notify.error('Could not reload the rule. Please retry.'); return; }
        const fresh = st.categories.flatMap((c) => c.rules).find((r) => r.code === code);
        if (!fresh) { notify.warning('This rule no longer exists.'); setDetail(null); return; }
        setDetailMode('view'); setDetail(fresh); setDetailKey((k) => k + 1);
        notify.success('Rule refreshed.');
      });
    };
    return <RuleDetailPage key={detailKey} rule={detail} categoryName={dCat} initialMode={detailMode} onBack={() => setDetail(null)}
      onRefresh={refreshDetail}
      onDeleted={() => { deleteRuleCodes([detail.code]); setSel(new Set()); setDetail(null); }} />;
  }

  return (
    <div className="nc-root">
      <NcTopbar icon={cat ? cat.icon : 'ti-adjustments-horizontal'} title={title} auditFormName={NotificationRulesAuditForm}>
        {cat && <button className="ca-btn" onClick={() => onOpen?.('rules')}><i className="ti ti-x" /> Clear filter</button>}
        <button className="ca-btn" onClick={() => setWizard(true)} title={`New (${formatCombo('alt+n')})`}><span className="tb-i">＋</span> New</button>
        <button className="ca-btn" onClick={doEdit} title={`Edit (${formatCombo('alt+e')})`}><span className="tb-i tb-i-blue">✎</span> Edit</button>
        <button className="ca-btn" onClick={doDelete} title={`Delete (${formatCombo('alt+d')})`}><span className="tb-i tb-i-red">🗑</span> Delete</button>
        <button className="ca-btn" onClick={refresh} title={`Refresh (${formatCombo('alt+r')})`}><span className="tb-i tb-i-green">🔄</span> Refresh</button>
        <ExportButton onPick={(f) => notify.info(`Exporting rules to ${f}…`)} />
      </NcTopbar>
      <div className="nc-scroll">
        {filterCode
          ? null   /* blue filter banner removed (user request 2026-09-19) */
          : (
            <div className="nc-kpi-row">
              <div className="nc-kpi"><div className="nc-kpi-lbl">Total Rules</div><div className="nc-kpi-val">{loaded ? sum.totalRules : '—'}</div><div className="nc-kpi-meta">{loaded ? <>Across {sum.categories} categories · {sum.outbound} outbound</> : 'Loading…'}</div></div>
              <div className="nc-kpi green"><div className="nc-kpi-lbl">Active</div><div className="nc-kpi-val green">{loaded ? sum.activeRules : '—'}</div><div className="nc-kpi-meta">{loaded ? <>{sum.disabledRules} disabled — enable as needed</> : 'Loading…'}</div></div>
              <div className="nc-kpi crit"><div className="nc-kpi-lbl">Require Acknowledgment</div><div className="nc-kpi-val crit">{loaded ? sum.requireAck : '—'}</div><div className="nc-kpi-meta">Regulatory exceedance + hold-time</div></div>
              <div className="nc-kpi info"><div className="nc-kpi-lbl">Need Recipients</div><div className="nc-kpi-val">{loaded ? sum.needRecipients : '—'}</div><div className="nc-kpi-meta">Set before they can send</div></div>
            </div>
          )}
        <div className="nc-toolrow">
          <SearchBox value={search} onChange={setSearch} placeholder="Search rules…" />
          <div className={`nc-chip${chip === 'all' ? ' active' : ''}`} onClick={() => setChip('all')}><i className="ti ti-list" /> All</div>
          <div className={`nc-chip${chip === 'internal' ? ' active' : ''}`} onClick={() => setChip('internal')}><span className="nc-chip-dot" style={{ background: 'var(--nc-text-light)' }} /> Internal</div>
          <div className={`nc-chip${chip === 'external' ? ' active' : ''}`} onClick={() => setChip('external')}><span className="nc-chip-dot" style={{ background: 'var(--nc-blue)' }} /> External</div>
          <div className={`nc-chip${chip === 'disabled' ? ' active' : ''}`} onClick={() => setChip('disabled')}><i className="ti ti-toggle-left" /> Disabled</div>
        </div>
        <div className="nc-table-wrap">
          <table className="nc-tbl">
            <thead><tr>
              <th className="nc-cb"><input type="checkbox" checked={allChecked} onChange={(e) => setSel(e.target.checked ? new Set(allCodes) : new Set())} /></th>
              <th>Rule Name</th><th>Rule Code</th><th>Purpose</th><th>Severity</th><th>Targets</th><th>Recipients</th><th>Ack</th><th>Enabled</th>
            </tr></thead>
            <tbody>
              {!loaded ? (
                <tr><td colSpan={9} className="nc-state-row"><span className="nc-inline-spin" /> Loading notification rules…</td></tr>
              ) : error ? (
                <tr><td colSpan={9} className="nc-state-row nc-state-err"><i className="ti ti-alert-triangle" /> Could not load notification rules from the server. <button className="nc-linkbtn" onClick={refresh}>Retry</button></td></tr>
              ) : (
                <>
                  {viewCats.map((c) => (
                    <RuleGroupRows key={c.code} cat={c} sel={sel} onToggleSel={toggleSel} onOpenDetail={(r) => openDetail(r, 'view')} onToggleRule={(code) => {
                      // Turning a rule ON needs it complete (owner request 2026-10-24); the server enforces the same.
                      const r = c.rules.find((x) => x.code === code);
                      const missing = r && !r.enabled ? savedRuleMissing(r, categoryCodeLabel(c)) : [];
                      if (missing.length) { notify.warning(incompleteMessage(missing)); return; }
                      toggleRule(code);
                    }} />
                  ))}
                  {shownRules.length === 0 && <tr><td colSpan={9} className="nc-state-row">{term ? <>No rules match “{search}”.</> : 'No notification rules yet.'}</td></tr>}
                </>
              )}
            </tbody>
          </table>
          <div className="nc-footer">
            <div className="nc-foot-left">{!loaded
              ? 'Loading…'
              : error
                ? 'Not loaded'
                : filterCode
                  ? `Showing ${shownRules.length} rule${shownRules.length === 1 ? '' : 's'} in ${cat?.name}`
                  : `Showing ${shownRules.length} of ${sum.totalRules} rules · ${sum.activeRules} enabled · ${sum.disabledRules} disabled`}</div>
            <div className="nc-pg"><button className="nc-pg-btn active">1</button></div>
          </div>
        </div>
      </div>
      {wizard && <NewRuleWizard categories={categories} defaultCategory={cat?.name} onClose={() => setWizard(false)}
        onCreate={(cn, r) => createRule(cn, r, () => setWizard(false))} />}
    </div>
  );
}

function RuleGroupRows({ cat, sel, onToggleSel, onOpenDetail, onToggleRule }: {
  cat: NcCategory; sel: Set<string>; onToggleSel: (code: string) => void; onOpenDetail: (r: NcRule) => void; onToggleRule: (code: string) => void;
}) {
  return (
    <>
      <tr className="nc-catgroup"><td colSpan={9}><i className={`ti ${cat.icon}`} />{cat.name}<span className="nc-catgroup-count">{cat.rules.length} rules</span></td></tr>
      {cat.rules.map((r) => (
        <tr key={r.code}>
          <td className="nc-cb"><input type="checkbox" checked={sel.has(r.code)} onChange={() => onToggleSel(r.code)} /></td>
          <td><span className="nc-rowlink" onClick={() => onOpenDetail(r)}>{r.name}</span></td>
          <td><span className="nc-mono">{r.code}</span></td>
          <td><span className={`nc-pp ${r.purpose}`}>{PURPOSE_LABEL[r.purpose]}</span></td>
          <td>{r.outbound ? <span className="nc-comm"><span className="dot" />Outbound</span> : r.severity ? <SevPill s={r.severity} /> : <span className="nc-dash">—</span>}</td>
          <td>{r.targets ? <span className="nc-tag">{r.targets}</span> : <span className="nc-dash">—</span>}</td>
          <td>{r.externalRecipient ? <span className="nc-ext">{r.recipients}</span> : r.recipients}</td>
          <td>{effectiveAck(r.notifyInApp, r.ack) ? <i className="ti ti-check" style={{ color: 'var(--nc-crit)' }} /> : <span className="nc-dash">—</span>}</td>
          <td><span className={`nc-toggle${r.enabled ? ' on' : ''}`} role="button" title="Toggle enabled" onClick={() => onToggleRule(r.code)} /></td>
        </tr>
      ))}
    </>
  );
}

/* ════════════════════ RULE CATEGORIES ════════════════════ */
function CategoriesPage() {
  const categories = useNcStore((s) => s.categories);
  const loaded = useNcStore((s) => s.loaded);
  const error = useNcStore((s) => s.error);
  const addCategory = useNcStore((s) => s.addCategory);
  const updateCategory = useNcStore((s) => s.updateCategory);
  const deleteCategoryCodes = useNcStore((s) => s.deleteCategoryCodes);
  const notify = useNotify();
  const { confirm } = useConfirm();
  const refresh = () => { void useNcStore.getState().load().then(() => notify.success('Categories refreshed.')); };
  const [sel, setSel] = useState<Set<number>>(new Set());
  const [form, setForm] = useState<{ open: boolean; edit?: NcCategory }>({ open: false });

  // No client-side nextCode: the Category Code (RC-NN) is minted by the server on
  // create — a browser-side max+1 let two tabs collide and re-issued deleted codes.
  const nextOrder = Math.max(0, ...categories.map((c) => c.order)) + 10;
  const activeCount = categories.filter((c) => c.active).length;
  const toggleSel = (code: number) => setSel((cur) => { const n = new Set(cur); if (n.has(code)) n.delete(code); else n.add(code); return n; });

  function del(codes: number[]) {
    if (!codes.length) { notify.warning('Select one or more categories to delete.'); return; }
    const blocked = categories.find((c) => codes.includes(c.code) && c.rules.length > 0);
    if (blocked) { notify.error(`“${blocked.name}” still has ${blocked.rules.length} rule(s) — a category can only be deleted once no rule references it.`); return; }
    confirm({ title: 'Delete category?', message: `Delete ${codes.length} categor${codes.length === 1 ? 'y' : 'ies'}?`, tone: 'danger', confirmLabel: 'Delete' })
      .then((ok) => { if (ok) { deleteCategoryCodes(codes); setSel(new Set()); notify.success(codes.length === 1 ? 'Category deleted.' : `${codes.length} categories deleted.`); } });
  }
  function doEdit() {
    if (sel.size !== 1) { notify.warning(sel.size ? 'Select just one category to edit.' : 'Select a category to edit.'); return; }
    setForm({ open: true, edit: categories.find((c) => c.code === [...sel][0]) });
  }

  // Keyboard shortcuts (CLAUDE.md standing rule 14, §18.6, ADR-0013): the
  // hand-rolled Categories toolbar.  Registered unconditionally above the return;
  // each def mirrors its button's handler (the buttons guard the selection
  // internally, so no disabled state to mirror).
  useHotkeys([
    { combo: 'alt+n', label: 'New Category', group: 'Notification Center', order: 1, run: () => setForm({ open: true }) },
    { combo: 'alt+e', label: 'Edit category', group: 'Notification Center', order: 2, run: () => doEdit() },
    { combo: 'alt+d', label: 'Delete category', group: 'Notification Center', order: 3, run: () => del([...sel]) },
    { combo: 'alt+r', label: 'Refresh', group: 'Notification Center', order: 5, run: refresh },
  ]);

  return (
    <div className="nc-root">
      <NcTopbar icon="ti-category-2" title="Rule Categories" auditFormName={NotificationSettingsAuditForms.categories}>
        <button className="ca-btn" onClick={() => setForm({ open: true })} title={`New (${formatCombo('alt+n')})`}><span className="tb-i">＋</span> New</button>
        <button className="ca-btn" onClick={doEdit} title={`Edit (${formatCombo('alt+e')})`}><span className="tb-i tb-i-blue">✎</span> Edit</button>
        <button className="ca-btn" onClick={() => del([...sel])} title={`Delete (${formatCombo('alt+d')})`}><span className="tb-i tb-i-red">🗑</span> Delete</button>
        <button className="ca-btn" onClick={refresh} title={`Refresh (${formatCombo('alt+r')})`}><span className="tb-i tb-i-green">🔄</span> Refresh</button>
        <ExportButton onPick={(f) => notify.info(`Exporting categories to ${f}…`)} />
      </NcTopbar>
      <div className="nc-scroll">
        <div className="nc-table-wrap">
          <table className="nc-tbl">
            <thead><tr><th className="nc-cb" /><th>Category Name</th><th>Category Code</th><th>Icon</th><th>Rules</th><th>Order</th><th>Active</th></tr></thead>
            <tbody>
              {!loaded ? (
                <tr><td colSpan={7} className="nc-state-row"><span className="nc-inline-spin" /> Loading categories…</td></tr>
              ) : error ? (
                <tr><td colSpan={7} className="nc-state-row nc-state-err"><i className="ti ti-alert-triangle" /> Could not load categories from the server. <button className="nc-linkbtn" onClick={refresh}>Retry</button></td></tr>
              ) : categories.length === 0 ? (
                <tr><td colSpan={7} className="nc-state-row">No categories yet.</td></tr>
              ) : categories.map((c) => (
                <tr key={c.code}>
                  <td className="nc-cb"><input type="checkbox" checked={sel.has(c.code)} onChange={() => toggleSel(c.code)} /></td>
                  <td>{c.name}</td>
                  <td><span className="nc-mono">{c.codeLabel}</span></td>
                  <td><span className="nc-iccell"><i className={`ti ${c.icon}`} /></span></td>
                  <td><span className="nc-tag">{c.rules.length}</span></td>
                  <td>{c.order}</td>
                  <td><span className={`nc-toggle${c.active ? ' on' : ''}`} role="button" title="Toggle active" onClick={() => updateCategory(c.code, { active: !c.active })} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="nc-footer"><div className="nc-foot-left">{loaded ? <>{categories.length} categories · {activeCount} active</> : 'Loading…'}</div><div className="nc-pg"><button className="nc-pg-btn active">1</button></div></div>
        </div>
      </div>
      {form.open && <CategoryForm initial={form.edit} nextOrder={nextOrder} onClose={() => setForm({ open: false })}
        onSave={(c) => {
          if (form.edit) { updateCategory(form.edit.code, c); notify.success('Category updated.'); setForm({ open: false }); return; }
          // Create waits for the server: it mints the Category Code the new row shows.
          void addCategory(c).then((ok) => { if (ok) { notify.success('Category created.'); setForm({ open: false }); } });
        }} />}
    </div>
  );
}

/* ════════════════════ RECIPIENT GROUPS ════════════════════ */
function RecipientsPage() {
  const recipients = useNcStore((s) => s.recipients);
  const addRecipient = useNcStore((s) => s.addRecipient);
  const updateRecipient = useNcStore((s) => s.updateRecipient);
  const deleteRecipientNames = useNcStore((s) => s.deleteRecipientNames);
  const notify = useNotify();
  const { confirm } = useConfirm();
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [form, setForm] = useState<{ open: boolean; edit?: typeof recipients[number]; mode?: 'view' | 'edit' }>({ open: false });
  const toggleSel = (name: string) => setSel((cur) => { const n = new Set(cur); if (n.has(name)) n.delete(name); else n.add(name); return n; });

  function del(names: string[]) {
    if (!names.length) { notify.warning('Select one or more groups to delete.'); return; }
    confirm({ title: 'Delete recipient group?', message: `Delete ${names.length} group${names.length === 1 ? '' : 's'}?`, tone: 'danger', confirmLabel: 'Delete' })
      .then((ok) => { if (ok) { deleteRecipientNames(names); setSel(new Set()); notify.success(names.length === 1 ? 'Group deleted.' : `${names.length} groups deleted.`); } });
  }
  function doEdit() {
    if (sel.size !== 1) { notify.warning(sel.size ? 'Select just one group to edit.' : 'Select a group to edit.'); return; }
    setForm({ open: true, edit: recipients.find((r) => r.name === [...sel][0]), mode: 'edit' });
  }

  // Keyboard shortcuts (CLAUDE.md standing rule 14, §18.6, ADR-0013): the
  // hand-rolled Recipient Groups toolbar.  Registered unconditionally above the
  // return; each def mirrors its button's handler.
  useHotkeys([
    { combo: 'alt+n', label: 'New Group', group: 'Notification Center', order: 1, run: () => setForm({ open: true, mode: 'edit' }) },
    { combo: 'alt+e', label: 'Edit group', group: 'Notification Center', order: 2, run: () => doEdit() },
    { combo: 'alt+d', label: 'Delete group', group: 'Notification Center', order: 3, run: () => del([...sel]) },
    { combo: 'alt+r', label: 'Refresh', group: 'Notification Center', order: 5, run: () => { useNcStore.getState().refreshGroups(); notify.success('Recipient groups refreshed.'); } },
  ]);

  return (
    <div className="nc-root">
      <NcTopbar icon="ti-users-group" title="Recipient Groups" auditFormName={NotificationSettingsAuditForms.recipientGroups}>
        <button className="ca-btn" onClick={() => setForm({ open: true, mode: 'edit' })} title={`New (${formatCombo('alt+n')})`}><span className="tb-i">＋</span> New</button>
        <button className="ca-btn" onClick={doEdit} title={`Edit (${formatCombo('alt+e')})`}><span className="tb-i tb-i-blue">✎</span> Edit</button>
        <button className="ca-btn" onClick={() => del([...sel])} title={`Delete (${formatCombo('alt+d')})`}><span className="tb-i tb-i-red">🗑</span> Delete</button>
        <button className="ca-btn" onClick={() => { useNcStore.getState().refreshGroups(); notify.success('Recipient groups refreshed.'); }} title={`Refresh (${formatCombo('alt+r')})`}><span className="tb-i tb-i-green">🔄</span> Refresh</button>
        <ExportButton onPick={(f) => notify.info(`Exporting recipient groups to ${f}…`)} />
      </NcTopbar>
      <div className="nc-scroll">
        <div className="nc-table-wrap">
          <table className="nc-tbl">
            <thead><tr><th className="nc-cb" /><th>Group Name</th><th>Members</th><th>Channels</th><th>Used by</th><th style={{ textAlign: 'right' }}>Actions</th></tr></thead>
            <tbody>
              {recipients.map((g) => (
                <tr key={g.name}>
                  <td className="nc-cb"><input type="checkbox" checked={sel.has(g.name)} onChange={() => toggleSel(g.name)} /></td>
                  <td><span className="nc-rowlink" onClick={() => setForm({ open: true, edit: g, mode: 'view' })}>{g.name}</span></td>
                  <td style={{ color: 'var(--nc-text-mid)' }}>{g.members}</td>
                  <td>{g.channels.map((ch) => <span key={ch} className="nc-chan on" style={{ marginRight: 4 }}>{ch}</span>)}</td>
                  <td><span className="nc-tag">{g.usedBy} rules</span></td>
                  <td><div className="nc-rowacts">
                    <button className="nc-iconbtn" title="Edit" onClick={() => setForm({ open: true, edit: g, mode: 'edit' })}><i className="ti ti-edit" /></button>
                    <button className="nc-iconbtn del" title="Delete" onClick={() => del([g.name])}><i className="ti ti-trash" /></button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="nc-footer"><div className="nc-foot-left">{recipients.length} groups</div><div className="nc-pg"><button className="nc-pg-btn active">1</button></div></div>
        </div>
      </div>
      {form.open && <RecipientForm initial={form.edit} mode={form.mode} onClose={() => setForm({ open: false })}
        onSave={(g) => { if (form.edit) { updateRecipient(form.edit.name, g); notify.success('Group updated.'); } else { addRecipient(g); notify.success('Group created.'); } }} />}
    </div>
  );
}

/* ════════════════════ EMAIL TEMPLATES ════════════════════ */
function TemplatesPage() {
  const categories = useNcStore((s) => s.categories);
  const templates = useNcStore((s) => s.templates);
  const addTemplate = useNcStore((s) => s.addTemplate);
  const updateTemplate = useNcStore((s) => s.updateTemplate);
  const deleteTemplateNames = useNcStore((s) => s.deleteTemplateNames);
  const notify = useNotify();
  const { confirm } = useConfirm();
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [form, setForm] = useState<{ open: boolean; edit?: typeof templates[number] }>({ open: false });
  const toggleSel = (name: string) => setSel((cur) => { const n = new Set(cur); if (n.has(name)) n.delete(name); else n.add(name); return n; });
  const activeCount = templates.filter((t) => t.active).length;

  function del(names: string[]) {
    if (!names.length) { notify.warning('Select one or more templates to delete.'); return; }
    confirm({ title: 'Delete template?', message: `Delete ${names.length} template${names.length === 1 ? '' : 's'}?`, tone: 'danger', confirmLabel: 'Delete' })
      .then((ok) => { if (ok) { deleteTemplateNames(names); setSel(new Set()); notify.success(names.length === 1 ? 'Template deleted.' : `${names.length} templates deleted.`); } });
  }
  function doEdit() {
    if (sel.size !== 1) { notify.warning(sel.size ? 'Select just one template to edit.' : 'Select a template to edit.'); return; }
    setForm({ open: true, edit: templates.find((t) => t.name === [...sel][0]) });
  }

  // Keyboard shortcuts (CLAUDE.md standing rule 14, §18.6, ADR-0013): the
  // hand-rolled Email Templates toolbar.  Registered unconditionally above the
  // return; each def mirrors its button's handler.
  useHotkeys([
    { combo: 'alt+n', label: 'New Template', group: 'Notification Center', order: 1, run: () => setForm({ open: true }) },
    { combo: 'alt+e', label: 'Edit template', group: 'Notification Center', order: 2, run: () => doEdit() },
    { combo: 'alt+d', label: 'Delete template', group: 'Notification Center', order: 3, run: () => del([...sel]) },
    { combo: 'alt+r', label: 'Refresh', group: 'Notification Center', order: 5, run: () => notify.success('Templates refreshed.') },
  ]);

  return (
    <div className="nc-root">
      <NcTopbar icon="ti-mail" title="Email Templates" auditFormName={NotificationSettingsAuditForms.emailTemplates}>
        <button className="ca-btn" onClick={() => setForm({ open: true })} title={`New (${formatCombo('alt+n')})`}><span className="tb-i">＋</span> New</button>
        <button className="ca-btn" onClick={doEdit} title={`Edit (${formatCombo('alt+e')})`}><span className="tb-i tb-i-blue">✎</span> Edit</button>
        <button className="ca-btn" onClick={() => del([...sel])} title={`Delete (${formatCombo('alt+d')})`}><span className="tb-i tb-i-red">🗑</span> Delete</button>
        <button className="ca-btn" onClick={() => notify.success('Templates refreshed.')} title={`Refresh (${formatCombo('alt+r')})`}><span className="tb-i tb-i-green">🔄</span> Refresh</button>
        <ExportButton onPick={(f) => notify.info(`Exporting templates to ${f}…`)} />
      </NcTopbar>
      <div className="nc-scroll">
        <div className="nc-table-wrap">
          <table className="nc-tbl">
            <thead><tr><th className="nc-cb" /><th>Template Name</th><th>Category</th><th>Subject</th><th>Active</th><th style={{ textAlign: 'right' }}>Actions</th></tr></thead>
            <tbody>
              {templates.map((t) => (
                <tr key={t.name}>
                  <td className="nc-cb"><input type="checkbox" checked={sel.has(t.name)} onChange={() => toggleSel(t.name)} /></td>
                  <td><span className="nc-rowlink" onClick={() => setForm({ open: true, edit: t })}>{t.name}</span></td>
                  <td>{t.category}</td>
                  <td style={{ color: 'var(--nc-text-mid)' }}>{t.subject}</td>
                  <td><span className={`nc-toggle${t.active ? ' on' : ''}`} role="button" title="Toggle active" onClick={() => updateTemplate(t.name, { active: !t.active })} /></td>
                  <td><div className="nc-rowacts">
                    <button className="nc-iconbtn" title="Edit" onClick={() => setForm({ open: true, edit: t })}><i className="ti ti-edit" /></button>
                    <button className="nc-iconbtn del" title="Delete" onClick={() => del([t.name])}><i className="ti ti-trash" /></button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="nc-footer"><div className="nc-foot-left">{templates.length} templates · {activeCount} active</div><div className="nc-pg"><button className="nc-pg-btn active">1</button></div></div>
        </div>
      </div>
      {form.open && <TemplateForm initial={form.edit} categories={categories.map((c) => ({ code: c.code, name: c.name }))} onClose={() => setForm({ open: false })}
        onSave={(t) => { if (form.edit) { updateTemplate(form.edit.name, t); notify.success('Template updated.'); } else { addTemplate(t); notify.success('Template created.'); } setForm({ open: false }); }} />}
    </div>
  );
}

/* ════════════════════ DELIVERY LOG ════════════════════ */
const DELIVERY_PAGE_SIZE = 20;

/** The Send Again confirmation (RTCFR 2026-10-16 wording). */
const SEND_AGAIN_CONFIRM = 'This email was already sent successfully. Do you want to send it again?';

/** Delivery Log — 100% DB-backed (dbo.AI_NotificationDeliveryLog). Server-side
 *  pagination, real counts, no mock rows. The Actions column re-sends one email
 *  (Resend for an undelivered one, Send Again for a sent one): only the row id goes to
 *  the API, which re-reads the original recipient / subject / body and logs the attempt
 *  as a NEW row — the original row is never changed. */
function DeliveryPage() {
  const notify = useNotify();
  const { confirm, alert } = useConfirm();
  // Same rule as the API gate: the Notification Center view's Write right under Module
  // Access Control; open for admins and when the lab set no MAC level for it.
  const canWrite = useAuthStore((s) => {
    const p = s.permissions;
    if (!p || p.isAdmin) return true;
    return p.navActions?.['ai_notifications_listview']?.write ?? true;
  });
  const [busyId, setBusyId] = useState<string | null>(null);
  // Synchronous guard: a second click before React re-renders can never start a second send.
  const sending = useRef(false);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<NcDeliveryPage | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchPage = (p: number) => {
    setLoading(true);
    getDeliveries(p, DELIVERY_PAGE_SIZE)
      .then((d) => { setData(d); setPage(d.page); })
      .catch(() => setData({ items: [], total: 0, failed: 0, last24h: 0, failed24h: 0, page: p, pageSize: DELIVERY_PAGE_SIZE }))
      .finally(() => setLoading(false));
  };
  useEffect(() => { fetchPage(1); }, []);

  const rows: NcDelivery[] = data?.items ?? [];
  const total = data?.total ?? 0;
  const failed = data?.failed ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / DELIVERY_PAGE_SIZE));

  function exportCsv(fmt: string) {
    if (rows.length === 0) { notify.warning('Nothing to export.'); return; }
    const head = ['Time', 'Rule', 'Subject', 'Channel', 'Role', 'Recipient', 'Status', 'Acknowledged By', 'Acknowledged At', 'Resolved At', 'Detail'];
    const esc = (v: string) => `"${(v ?? '').replace(/"/g, '""')}"`;
    const lines = [head.join(',')].concat(rows.map((d) =>
      [fmtLocalShort(d.time), d.rule, d.subject, d.channel, d.role, d.recipient, d.status,
        d.acknowledgedBy ?? '', d.acknowledgedAt ? fmtLocalShort(d.acknowledgedAt) : '', d.resolvedAt ? fmtLocalShort(d.resolvedAt) : '', d.detail].map((c) => esc(String(c ?? ''))).join(',')));
    const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `delivery-log-p${page}.csv`; a.click();
    URL.revokeObjectURL(url);
    notify.success(`Exported to ${fmt} successfully.`);
  }

  useHotkeys([
    { combo: 'alt+r', label: 'Refresh', group: 'Notification Center', order: 5,
      run: () => { fetchPage(page); notify.success('Delivery log refreshed.'); } },
  ]);

  async function resend(d: NcDelivery) {
    if (!d.id || !d.action || sending.current) return;
    const action = d.action;
    const again = action === 'SendAgain';
    const ok = await confirm(again
      ? { title: 'Send Again', message: SEND_AGAIN_CONFIRM, confirmLabel: 'Send Again', cancelLabel: 'Cancel' }
      : { title: 'Resend', message: `Do you want to resend this email to ${d.recipient}?`, confirmLabel: 'Resend', cancelLabel: 'Cancel' });
    if (!ok || sending.current) return;
    sending.current = true;
    setBusyId(d.id);
    try {
      const r = await runLoading(resendDelivery(d.id, action), { blocking: true, label: again ? 'Sending email again…' : 'Resending email…' });
      if (r.status === 'Sent') {
        notify.success(`${again ? 'Email sent again' : 'Email resent'} successfully.${r.detail ? ` ${r.detail}` : ''}`);
      } else {
        await alert({
          tone: r.status === 'Failed' ? 'error' : 'warning',
          title: 'Email not sent',
          message: `${r.detail || 'The email could not be sent.'} The attempt is recorded in the Delivery Log.`,
        });
      }
      fetchPage(1);                       // the new attempt is the newest row
    } catch (err) {
      await alert({ tone: 'error', title: 'Email not sent', message: apiErrorText(err, 'The email could not be sent. Try again.') });
      fetchPage(page);
    } finally {
      sending.current = false;
      setBusyId(null);
    }
  }
  return (
    <div className="nc-root">
      <NcTopbar icon="ti-mail-fast" title="Delivery Log">
        <button className="ca-btn" onClick={() => { fetchPage(page); notify.success('Delivery log refreshed.'); }} title={`Refresh (${formatCombo('alt+r')})`}><span className="tb-i tb-i-green">🔄</span> Refresh</button>
        <ExportButton onPick={exportCsv} />
      </NcTopbar>
      <div className="nc-scroll">
        <div className="nc-table-wrap">
          <table className="nc-tbl">
            <thead><tr><th>Time</th><th>Rule</th><th>Subject</th><th>Channel</th><th>Role</th><th>Recipient(s)</th><th>Status</th><th>Acknowledged By</th><th>Acknowledged At</th><th>Resolved At</th><th>Detail</th><th className="nc-dl-actcol">Actions</th></tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={12} style={{ textAlign: 'center', color: 'var(--nc-text-light)', padding: '18px 0' }}>{loading ? 'Loading…' : 'No delivery records yet — entries appear here when a notification is sent (email or in-app).'}</td></tr>}
              {rows.map((d, i) => (
                <tr key={d.id ?? i}>
                  <td className="nc-mono">{fmtLocalShort(d.time)}</td>
                  <td>{d.rule || '—'}</td>
                  <td style={{ color: 'var(--nc-text-mid)' }}>{d.subject || '—'}</td>
                  <td>{d.channel || '—'}</td>
                  <td>{d.role || '—'}</td>
                  <td>{d.recipient || '—'}</td>
                  <td><DeliveryPill s={d.status} /></td>
                  <AckCells d={d} />
                  <td className="nc-mono" title={d.resolvedAt ? 'Auto-resolved: the condition cleared.' : undefined}>{d.resolvedAt ? fmtLocalShort(d.resolvedAt) : '—'}</td>
                  <td style={{ color: 'var(--nc-text-mid)' }}>
                    {d.attemptType && (
                      <div className="nc-dl-attempt">
                        {d.attemptType}{d.attemptedBy ? ` by ${d.attemptedBy}` : ''}{d.originalTime ? ` · of the ${fmtLocalShort(d.originalTime)} email` : ''}
                      </div>
                    )}
                    {d.detail || (d.attemptType ? '' : '—')}
                  </td>
                  <td className="nc-dl-actcol">
                    <DeliveryActionButton d={d} busy={busyId === d.id} locked={busyId !== null} canWrite={canWrite} onClick={() => void resend(d)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="nc-footer">
            <div className="nc-foot-left">Page {page} of {pageCount} ({total} deliveries · {failed} failed)</div>
            <div className="nc-pg">
              <button className="nc-pg-btn" disabled={page <= 1} onClick={() => fetchPage(page - 1)}>‹</button>
              <button className="nc-pg-btn active">{page}</button>
              <button className="nc-pg-btn" disabled={page >= pageCount} onClick={() => fetchPage(page + 1)}>›</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Acknowledged By / Acknowledged At for one Delivery Log row. Only In-App alerts can be
 *  acknowledged — by their recipient, on their own copy — so email rows show "—"; an In-App alert
 *  that needs it but has none yet reads "Awaiting" — unless it auto-resolved, which ends the wait. */
function AckCells({ d }: { d: NcDelivery }) {
  if (d.acknowledgedAt)
    return <><td>{d.acknowledgedBy || d.recipient || '—'}</td><td className="nc-mono">{fmtLocalShort(d.acknowledgedAt)}</td></>;
  const awaiting = d.channel === 'In-App' && d.ackRequired && !d.resolvedAt;
  return <><td className={awaiting ? 'nc-dl-awaiting' : undefined}>{awaiting ? 'Awaiting' : '—'}</td><td>—</td></>;
}

/** One row's Actions button: Resend (not delivered) · Send Again (Sent) · disabled (no
 *  action — pending, In-App, content not stored — with the API's reason as the tooltip). */
function DeliveryActionButton({ d, busy, locked, canWrite, onClick }: {
  d: NcDelivery; busy: boolean; locked: boolean; canWrite: boolean; onClick: () => void;
}) {
  // A disabled row still names the action its status maps to (a Sent row reads Send Again).
  const sendAgain = d.action ? d.action === 'SendAgain' : d.status === 'Sent';
  const label = sendAgain ? 'Send Again' : 'Resend';
  const title = !d.action ? (d.actionNote || 'No action for this record.')
    : !canWrite ? 'You do not have permission to send notification emails.'
    : d.action === 'SendAgain' ? 'Send this email to the same recipient again' : 'Retry sending this email to the same recipient';
  return (
    <button type="button" className="nc-btn nc-dl-act" disabled={!d.action || !canWrite || locked}
      aria-busy={busy || undefined} title={title} aria-label={`${label}${d.recipient ? ` to ${d.recipient}` : ''}`} onClick={onClick}>
      <BusyIcon busy={busy} icon={sendAgain ? 'ti-send' : 'ti-refresh'} /> {label}
    </button>
  );
}

/* ════════════════════ host ════════════════════ */
export default function NotificationCenterPage({ subId, onOpen }: { subId: string; onOpen?: (id: string) => void }) {
  // Re-hydrate rules+categories from the DB every time the Notification Center is
  // entered, so an external change (delete, edit, another session) is always
  // reflected — never a stale cache. (Low-volume data; the shared api client
  // drives the global loader.)
  useEffect(() => { void useNcStore.getState().load(); }, []);
  const catMatch = /^cat-(\d+)$/.exec(subId);
  if (catMatch) return <RulesPage onOpen={onOpen} filterCode={Number(catMatch[1])} />;
  switch (subId) {
    case 'rules': return <RulesPage onOpen={onOpen} />;
    case 'categories': return <CategoriesPage />;
    case 'recipients': return <RecipientsPage />;
    case 'templates': return <TemplatesPage />;
    case 'delivery': return <DeliveryPage />;
    case 'popup-settings': return <PopupSettingsPage />;
    default: return <OverviewPage onOpen={onOpen} />;
  }
}
